// ── <DeployedRevision> — deployed-revision block for the debug account panel ──
//
// Shows which commit is live for BOTH chatboard and the notifier. They run the
// same image but deploy as independent Cloud Run services, so their revisions
// (and, if one lags a deploy, their SHAs) can differ — hence two rows, not one.
// Pure presentational: `data` is the useDeployedRevision result
//   null                 → still loading (render "…")
//   { app, notifier }    → app: version info | null; notifier: { reachable, … }
// An unreachable notifier / failed app fetch renders as "unknown".

import React from "react";
import { C } from "../lib/colors.js";
import { formatServiceVersion, formatBuiltAt } from "../lib/version.js";

// One service's rows: a label + a stacked value (sha · revision on top, a small
// "built <date>" line below). Values wrap (never nowrap) so the block can't push
// the account panel into horizontal scroll on a narrow screen.
function ServiceRow({ label, version, builtAt, title }) {
  return (
    <>
      <span style={{ color: C.gray, letterSpacing: 0 }}>{label}</span>
      <span style={{ color: C.white, overflowWrap: "anywhere" }} title={title}>
        {version}
        {builtAt ? (
          <span style={{ display: "block", color: C.gray, fontSize: 11, marginTop: 2 }}>
            built {builtAt}
          </span>
        ) : null}
      </span>
    </>
  );
}

export default function DeployedRevision({ data }) {
  const app = data?.app;
  const notifier = data?.notifier;
  const notifierReachable = !!notifier?.reachable;

  return (
    <div style={{ marginTop: 16 }}>
      <div className="sy-kicker" style={{ marginBottom: 8 }}>
        Deployed revision
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "max-content 1fr",
          rowGap: 8,
          columnGap: 14,
          fontSize: 12,
          color: C.grayL,
          fontFamily: '"IBM Plex Mono",monospace',
        }}
      >
        <ServiceRow
          label="app"
          version={data ? formatServiceVersion(app) : "…"}
          builtAt={data ? formatBuiltAt(app?.builtAt) : ""}
          title={app?.gitSha || ""}
        />
        <ServiceRow
          label="notifier"
          version={!data ? "…" : notifierReachable ? formatServiceVersion(notifier) : "unknown"}
          builtAt={notifierReachable ? formatBuiltAt(notifier?.builtAt) : ""}
          title={notifierReachable ? notifier?.gitSha || "" : notifier?.reason || ""}
        />
      </div>
    </div>
  );
}
